import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import DocumentMeta from './DocumentMeta.jsx'
import { supabase } from '../lib/supabase'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError'

const STORAGE_KEY = 'panellhnies_target_schools_v1'

function formatScore(value) {
  if (value === null || value === undefined || value === '') return '—'
  return Number(value).toLocaleString('el-GR')
}

function getStatusFromGap(gap) {
  if (gap >= 500) return 'safe'
  if (gap <= -500) return 'reach'
  return 'borderline'
}

function statusLabel(status) {
  if (status === 'safe') return 'Ασφαλές'
  if (status === 'reach') return 'Δύσκολο'
  if (status === 'borderline') return 'Οριακό'
  if (status === 'pending') return '—'
  return 'Χωρίς δεδομένα'
}

function statusClass(status) {
  if (status === 'safe') return 'bg-green-50 text-green-700 border-green-200'
  if (status === 'reach') return 'bg-red-50 text-red-700 border-red-200'
  if (status === 'borderline') return 'bg-amber-50 text-amber-700 border-amber-200'
  return 'bg-gray-100 text-gray-600 border-gray-200'
}

function buildTargetId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return `target_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

async function fetchLatestScoresByDepartmentIds(departmentIds) {
  if (!departmentIds.length) return new Map()

  const { data, error } = await supabase
    .from('scores')
    .select('department_id, year, score')
    .in('department_id', departmentIds)
    .order('year', { ascending: false })
    .order('score', { ascending: false })

  if (error) throw error

  const latestByDept = new Map()
  for (const row of data || []) {
    const key = String(row.department_id)
    if (!latestByDept.has(key)) {
      latestByDept.set(key, { latestBase: row.score, year: row.year })
    }
  }
  return latestByDept
}

export default function TargetSchoolsPage() {
  const [estimatedMoria, setEstimatedMoria] = useState('')
  const [departmentSearch, setDepartmentSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [targets, setTargets] = useState([])
  const [message, setMessage] = useState('')
  const [hydrated, setHydrated] = useState(false)
  const [draggedId, setDraggedId] = useState(null)
  const [dropPreview, setDropPreview] = useState(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)

      if (typeof parsed?.estimatedMoria === 'number') {
        setEstimatedMoria(parsed.estimatedMoria)
      }

      if (Array.isArray(parsed?.targets)) {
        setTargets(parsed.targets)
      }
    } catch {
      // Ignore invalid saved payload and keep defaults.
    } finally {
      setHydrated(true)
    }
  }, [])

  useEffect(() => {
    if (!hydrated) return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          estimatedMoria: estimatedMoria === '' ? null : Number(estimatedMoria),
          targets,
        })
      )
    } catch {
      // Ignore storage failures (private mode/quota).
    }
  }, [estimatedMoria, targets, hydrated])

  const analyzedTargets = useMemo(() => {
    return targets.map((target) => {
      if (target.latestBase === null || target.latestBase === undefined) {
        return { ...target, gap: null, status: 'no-data' }
      }
      if (estimatedMoria === '') {
        return { ...target, gap: null, status: 'pending' }
      }

      const gap = Number(estimatedMoria) - Number(target.latestBase)
      return { ...target, gap, status: getStatusFromGap(gap) }
    })
  }, [targets, estimatedMoria])

  const summary = useMemo(() => {
    return analyzedTargets.reduce(
      (acc, item) => {
        if (item.status === 'safe') acc.safe += 1
        if (item.status === 'borderline') acc.borderline += 1
        if (item.status === 'reach') acc.reach += 1
        if (item.status === 'no-data') acc.noData += 1
        return acc
      },
      { total: analyzedTargets.length, safe: 0, borderline: 0, reach: 0, noData: 0 }
    )
  }, [analyzedTargets])

  const handleSearch = async (e) => {
    e.preventDefault()
    setMessage('')

    const q = departmentSearch.trim()
    if (q.length < 2) {
      setSearchResults([])
      setMessage('Γράψε τουλάχιστον 2 χαρακτήρες για αναζήτηση.')
      return
    }

    setSearchLoading(true)
    try {
      const { data, error } = await supabase.rpc('search_departments', { search_text: q })

      if (error) {
        reportError(error, { where: 'TargetSchoolsPage.searchDepartments', search_text: q })
        setSearchResults([])
        setMessage('Δεν ήταν δυνατή η αναζήτηση αυτή τη στιγμή.')
        return
      }

      const departments = data || []
      if (!departments.length) {
        setSearchResults([])
        setMessage('Δεν βρέθηκαν τμήματα με αυτό το κείμενο.')
        return
      }

      const departmentIds = departments
        .map((dept) => dept.id)
        .filter((id) => id !== null && id !== undefined)

      let latestScoreMap = new Map()
      try {
        latestScoreMap = await fetchLatestScoresByDepartmentIds(departmentIds)
      } catch (scoreError) {
        reportError(scoreError, { where: 'TargetSchoolsPage.searchDepartments.fetchScores', search_text: q })
      }

      const enrichedResults = departments
        .map((dept) => {
          const latestScore = latestScoreMap.get(String(dept.id))
          return {
            ...dept,
            searchLatestBase: latestScore?.latestBase ?? null,
            searchLatestYear: latestScore?.year ?? null,
          }
        })
        .sort((a, b) => {
          const aBase = Number(a.searchLatestBase)
          const bBase = Number(b.searchLatestBase)
          const aHasBase = Number.isFinite(aBase)
          const bHasBase = Number.isFinite(bBase)
          if (aHasBase && bHasBase) return bBase - aBase
          if (aHasBase) return -1
          if (bHasBase) return 1
          return (a.name || '').localeCompare(b.name || '', 'el')
        })

      setSearchResults(enrichedResults.slice(0, 15))
    } finally {
      setSearchLoading(false)
    }
  }

  const handleAddTarget = async (dept) => {
    setMessage('')

    const departmentSlug = dept.slug || String(dept.id)
    const alreadyExists = targets.some((x) => x.departmentSlug === departmentSlug)
    if (alreadyExists) {
      setMessage('Αυτό το τμήμα υπάρχει ήδη στους στόχους σου.')
      return
    }

    let latestBase = dept.searchLatestBase ?? null
    let year = dept.searchLatestYear ?? null

    if (latestBase === null) {
      const { data: latestScore, error } = await supabase
        .from('scores')
        .select('year, score')
        .eq('department_id', dept.id)
        .order('year', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        reportError(error, { where: 'TargetSchoolsPage.fetchLatestScore', department_id: dept.id })
      } else if (latestScore) {
        latestBase = latestScore.score
        year = latestScore.year
      }
    }

    const newTarget = {
      id: buildTargetId(),
      departmentSlug,
      departmentName: dept.name || 'Τμήμα',
      university: dept.university || '',
      city: dept.city || '',
      latestBase,
      year,
    }

    setTargets((prev) => [...prev, newTarget])
    trackEvent('target_school_added', {
      department_slug: departmentSlug,
      has_latest_base: latestBase !== null,
      latest_base_year: year,
    })
  }

  const handleRemoveTarget = (target) => {
    setTargets((prev) => prev.filter((x) => x.id !== target.id))
    trackEvent('target_school_removed', { department_slug: target.departmentSlug })
  }

  const handleClearAll = () => {
    setTargets([])
    trackEvent('target_schools_cleared', { source: 'target_schools_page' })
  }

  const handleDragStart = (id) => {
    setDraggedId(id)
  }

  const handleDragOver = (e, id) => {
    e.preventDefault()
    if (!draggedId || draggedId === id) return

    const rect = e.currentTarget.getBoundingClientRect()
    const position = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'

    if (dropPreview?.id !== id || dropPreview?.position !== position) {
      setDropPreview({ id, position })
    }
  }

  const handleDrop = (dropId) => {
    if (!draggedId || draggedId === dropId || !dropPreview) {
      setDraggedId(null)
      setDropPreview(null)
      return
    }

    setTargets((prev) => {
      const fromIndex = prev.findIndex((x) => x.id === draggedId)
      const toIndex = prev.findIndex((x) => x.id === dropId)
      if (fromIndex < 0 || toIndex < 0) return prev

      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)

      let insertAt = toIndex
      if (dropPreview.position === 'after') {
        insertAt = fromIndex < toIndex ? toIndex : toIndex + 1
      } else {
        insertAt = fromIndex < toIndex ? toIndex - 1 : toIndex
      }

      next.splice(insertAt, 0, moved)
      return next
    })

    trackEvent('target_schools_reordered', {
      source: 'target_schools_page',
    })

    setDraggedId(null)
    setDropPreview(null)
  }

  const handleDragEnd = () => {
    setDraggedId(null)
    setDropPreview(null)
  }

  const hasEstimated = estimatedMoria !== ''

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <DocumentMeta
        title="Στόχοι Σχολών | Πανελλήνιες+"
        description="Αποθήκευσε σχολές-στόχους και δες το κενό ανάμεσα στα μόριά σου και τις τελευταίες βάσεις."
        canonical="https://www.panellhniesplus.gr/stoxoi-sxolon"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Αρχική", item: "https://www.panellhniesplus.gr/" },
          { "@type": "ListItem", position: 2, name: "Στόχοι Σχολών", item: "https://www.panellhniesplus.gr/stoxoi-sxolon" },
        ],
      }) }} />

      <Header />

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-7">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Στόχοι Σχολών</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-3xl">
            Βάλε εκτιμώμενα μόρια, πρόσθεσε τμήματα που σε ενδιαφέρουν και δες άμεσα πόσο κοντά είσαι
            με βάση τα πιο πρόσφατα διαθέσιμα δεδομένα.
          </p>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <label htmlFor="estimated-moria" className="text-sm font-medium text-gray-800">
                Εκτιμώμενα μόρια
              </label>
              <input
                id="estimated-moria"
                type="number"
                min="0"
                max="20000"
                step="1"
                value={estimatedMoria}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '') {
                    setEstimatedMoria('')
                    return
                  }
                  const numeric = Number(value)
                  const clamped = Math.max(0, Math.min(20000, numeric))
                  setEstimatedMoria(Number.isNaN(clamped) ? '' : clamped)
                }}
                onBlur={() => {
                  if (estimatedMoria === '') return
                  trackEvent('target_score_updated', { estimated_moria: Number(estimatedMoria) })
                }}
                placeholder="π.χ. 16500"
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
              />
              <p className="mt-2 text-xs text-gray-600">
                Τα statuses εμφανίζονται όταν συμπληρώσεις μόρια.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 p-4 lg:col-span-2">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={departmentSearch}
                  onChange={(e) => setDepartmentSearch(e.target.value)}
                  placeholder="Αναζήτηση τμήματος (π.χ. Πληροφορική)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
                <button
                  type="submit"
                  disabled={searchLoading}
                  className="cursor-pointer rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-70"
                >
                  {searchLoading ? 'Αναζήτηση...' : 'Αναζήτηση'}
                </button>
              </form>

              {searchResults.length > 0 ? (
                <div className="mt-3 max-h-60 overflow-auto rounded-lg border border-gray-200">
                  {searchResults.map((dept) => {
                    const slug = dept.slug || String(dept.id)
                    const exists = targets.some((x) => x.departmentSlug === slug)

                    return (
                      <div key={slug} className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2.5 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{dept.name}</p>
                          <p className="text-xs text-gray-600">
                            {dept.university || 'Πανεπιστήμιο'}{dept.city ? ` • ${dept.city}` : ''}
                            {dept.searchLatestBase !== null ? ` • Βάση: ${formatScore(dept.searchLatestBase)} (${dept.searchLatestYear || '—'})` : ''}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={exists}
                          onClick={() => handleAddTarget(dept)}
                          className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {exists ? 'Προστέθηκε' : 'Προσθήκη'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : null}

              {message ? <p className="mt-3 text-xs text-gray-600">{message}</p> : null}
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base sm:text-lg font-semibold">Η λίστα στόχων σου</h2>

            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1">Σύνολο: {summary.total}</span>
              <span className="rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-green-700">Ασφαλές: {summary.safe}</span>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-700">Οριακό: {summary.borderline}</span>
              <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-red-700">Δύσκολο: {summary.reach}</span>
            </div>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            Ερμηνεία status: Ασφαλές (≥ +500 μόρια), Οριακό (-499 έως +499), Δύσκολο (≤ -500).
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Σημείωση: Οι στόχοι αποθηκεύονται μόνο τοπικά στον browser αυτής της συσκευής. Αν
            καθαρίσεις δεδομένα περιήγησης (cache/site data/local storage), αν αλλάξεις browser ή
            συσκευή, η λίστα δεν θα εμφανίζεται.
          </p>

          {analyzedTargets.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-600">
              Δεν έχεις προσθέσει ακόμα τμήματα. Χρησιμοποίησε την αναζήτηση πιο πάνω για να φτιάξεις τη λίστα σου.
            </div>
          ) : (
            <>
              <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-3 py-2 w-12">#</th>
                      <th className="px-3 py-2">Τμήμα</th>
                      <th className="px-3 py-2">Πόλη</th>
                      <th className="px-3 py-2">Τελευταία βάση</th>
                      <th className="px-3 py-2">Gap</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Ενέργεια</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyzedTargets.map((target, index) => {
                      const showGap = hasEstimated && target.gap !== null
                      const gapLabel = showGap
                        ? `${target.gap > 0 ? '+' : ''}${formatScore(target.gap)}`
                        : '—'
                      const isDragging = draggedId === target.id
                      const showTopPreview = dropPreview?.id === target.id && dropPreview?.position === 'before'
                      const showBottomPreview = dropPreview?.id === target.id && dropPreview?.position === 'after'

                      return (
                        <tr
                          key={target.id}
                          draggable
                          onDragStart={() => handleDragStart(target.id)}
                          onDragOver={(e) => handleDragOver(e, target.id)}
                          onDrop={() => handleDrop(target.id)}
                          onDragEnd={handleDragEnd}
                          className={`border-t border-gray-100 transition-colors ${
                            isDragging ? 'opacity-50 cursor-grabbing' : 'cursor-grab'
                          } ${showTopPreview || showBottomPreview ? 'bg-blue-50' : ''} ${
                            showTopPreview ? 'border-t-2 border-t-blue-500' : ''
                          } ${showBottomPreview ? 'border-b-2 border-b-blue-500' : ''}`}
                        >
                          <td className="px-3 py-2.5 text-xs font-semibold text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-3 py-2.5">
                            <Link
                              to={`/tmima/${target.departmentSlug}`}
                              className="font-medium text-gray-900 hover:text-gray-700 underline-offset-2 hover:underline"
                            >
                              {target.departmentName}
                            </Link>
                            <p className="text-xs text-gray-600">{target.university || '—'}</p>
                          </td>
                          <td className="px-3 py-2.5 text-gray-700">{target.city || '—'}</td>
                          <td className="px-3 py-2.5 text-gray-700">
                            {target.latestBase !== null ? `${formatScore(target.latestBase)} (${target.year || '—'})` : 'Χωρίς δεδομένα'}
                          </td>
                          <td className="px-3 py-2.5 text-gray-700">{gapLabel}</td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${statusClass(target.status)}`}>
                              {statusLabel(target.status)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <button
                              type="button"
                              onClick={() => handleRemoveTarget(target)}
                              className="cursor-pointer rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
                            >
                              Αφαίρεση
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
                >
                  Καθαρισμός λίστας
                </button>
              </div>
            </>
          )}
        </section>

      </main>

      <Footer />
    </div>
  )
}
